import { feedRowHasDate, listingFacts, type FeedScrape, type ListingFacts } from '../feed/listing.ts';
import type { PlaylistSummary } from '../model/playlist.ts';
import type { VideoSummary } from '../model/video.ts';
import type { ParseError } from '../result.ts';

/**
 * §8's Channel row: which tabs exist, what each one's rows are *allowed to claim*, and how the
 * continuations work.
 *
 * This is in core rather than in the channel screen for the reason `playback/input.ts` and
 * `search/query.ts` are: every line of it is a fact about the API measured on the wire, Compose has
 * to act on the identical facts, and a decision table written inside an event handler is a decision
 * table nobody tests.
 *
 * Everything below was measured against `invidious.example.com` on 2026-07-29 (API reference §7f).
 * Three of the measurements changed the design and are called out where they apply.
 */

/**
 * §8's five tabs. `community` is **not** here: it is explicitly deferred post-v1 (it needs a
 * `CommunityPost` entity and `/api/v1/post/*`), even though `/api/v1/channels/{ucid}/community`
 * answers 200 on this instance.
 *
 * `about` is ours rather than the API's — the data for it arrives with the channel record itself, so
 * it costs no request. Every other tab is one endpoint.
 */
export type ChannelTab = 'videos' | 'shorts' | 'streams' | 'playlists' | 'about';

/** The four tabs that fetch a list. `about` is drawn from the channel record. */
export type ChannelListTab = 'videos' | 'shorts' | 'streams' | 'playlists';

export const CHANNEL_TABS: readonly ChannelTab[] = ['videos', 'shorts', 'streams', 'playlists', 'about'];

export const CHANNEL_LIST_TABS: readonly ChannelListTab[] = ['videos', 'shorts', 'streams', 'playlists'];

export const DEFAULT_CHANNEL_TAB: ChannelTab = 'videos';

export function isChannelTab(value: string): value is ChannelTab {
  return (CHANNEL_TABS as readonly string[]).includes(value);
}

export function isChannelListTab(tab: ChannelTab): tab is ChannelListTab {
  return tab !== 'about';
}

/**
 * `sort_by` on the three video tabs, and **only** those three.
 *
 * Self-verified rather than assumed, because a `200` never proves a parameter was honoured (§2):
 * `oldest` came back with `published` **strictly ascending on 60 of 60 rows** (2011 → 2012 for a
 * channel whose first page otherwise starts in 2025), and `popular` came back with **one view-count
 * inversion in 59** — the one being two rows that both round to the same displayed figure. An
 * unrecognised value (`bogus`) returns the `newest` list, so the fallback here matches the server's.
 *
 * **`newest` is not monotonic in `published` and that is not a bug in the sort.** Four of 59 steps
 * invert, because `published` is back-computed from the coarse relative text: every row inside "1
 * month ago" resolves to the same second, and a row the scrape degraded resolves to *now*. So do not
 * write a test asserting the order — assert `oldest`, which the coarse text cannot break.
 */
export type ChannelVideoSort = 'newest' | 'oldest' | 'popular';

export const CHANNEL_VIDEO_SORTS: readonly ChannelVideoSort[] = ['newest', 'oldest', 'popular'];

export const DEFAULT_CHANNEL_SORT: ChannelVideoSort = 'newest';

export function normalizeChannelSort(value: string | null | undefined): ChannelVideoSort {
  return value !== null && value !== undefined && (CHANNEL_VIDEO_SORTS as readonly string[]).includes(value)
    ? (value as ChannelVideoSort)
    : DEFAULT_CHANNEL_SORT;
}

/**
 * Whether this tab's endpoint has a working sort — and the `false` arm is a measurement, not a
 * simplification.
 *
 * `/playlists` accepts `sort_by=last` (its default) and `newest`, and answers **`200 []`** for
 * `popular` *and* for any unrecognised value. That is the empty-`region=` trap in a third costume:
 * the parameter is not ignored, it empties the list, so a channel with 30 playlists reads as a
 * channel with none and nothing says why. The safe thing is to send no `sort_by` at all here, which
 * is what {@link channelTabWireParams} does.
 */
export function channelTabSupportsSort(tab: ChannelTab): boolean {
  return tab === 'videos' || tab === 'shorts' || tab === 'streams';
}

/**
 * The list tabs this record claims, with the fallback applied.
 *
 * An **empty or unreadable** `tabs` becomes all four: showing every tab is the recoverable mistake (one
 * of them is empty), showing none is not (the page has no content at all). Both public functions below
 * go through here so they cannot disagree about what "declared" means.
 */
function declaredListTabs(rawTabs: readonly string[]): readonly ChannelListTab[] {
  const declared = rawTabs.filter((tab): tab is ChannelListTab =>
    (CHANNEL_LIST_TABS as readonly string[]).includes(tab),
  );
  return declared.length === 0 ? CHANNEL_LIST_TABS : declared;
}

/**
 * Which of our tabs this channel actually has, from the response's own `tabs` array — **plus whichever
 * tab is being looked at**, declared or not.
 *
 * The filtering is load-bearing rather than cosmetic: `tabs` genuinely varies, and a tab it omits
 * answers `{"videos":[]}` forever. Measured — Kurzgesagt, Veritasium and Cocomelon have no `streams`;
 * PewDiePie has no `shorts`; Khan Academy has both plus `courses`. Verified in the omitted direction
 * too (2 of 2): `tabs` without `shorts` → `/shorts` returns an empty list, `tabs` without `streams` →
 * likewise. So filtering is what stops the page drawing a tab whose only possible content is
 * "nothing here".
 *
 * **`current` is why this takes a second parameter.** A route can name a tab the record
 * omits — `/channel/UC…/shorts` on a channel with no Shorts is a URL anyone can type, link or keep in a
 * bookmark from before the channel stopped publishing them — and filtering it out left the strip with
 * *no* tab marked current: four links, none of them `aria-current`, above an empty list. A reader
 * cannot tell where they are, and a screen reader is told nothing at all. So the tab being looked at is
 * always in the strip, and the page says why it is empty (`channelDeclaresTab`) instead of pretending
 * the reader is somewhere else.
 *
 * `about` is always present because its data is the record we already have.
 */
export function channelTabsFrom(
  rawTabs: readonly string[],
  current: ChannelTab | null,
): readonly ChannelTab[] {
  const lists = declaredListTabs(rawTabs);
  // §8's order, not the API's: `tabs` puts `playlists` after `podcasts` on some channels, and the tab
  // strip should not reorder itself per channel.
  return CHANNEL_TABS.filter(
    (tab) => tab === 'about' || tab === current || (lists as readonly string[]).includes(tab),
  );
}

/**
 * Whether the record itself claims this tab — the question `channelTabsFrom` used to answer by
 * omission, and which a page now has to ask separately because the tab it is showing may be there only
 * because somebody navigated to it.
 *
 * It is the difference between two empty lists that look identical: "this channel has published no
 * Shorts" and "this channel has no Shorts tab, so that endpoint will answer empty forever".
 */
export function channelDeclaresTab(rawTabs: readonly string[], tab: ChannelTab): boolean {
  return tab === 'about' || (declaredListTabs(rawTabs) as readonly string[]).includes(tab);
}

/* ------------------------------------------------------------------------------------------------
 * Requests
 * ---------------------------------------------------------------------------------------------- */

/** The API path for a list tab. `about` has none — it is the channel record. */
export function channelTabPath(channelId: string, tab: ChannelListTab): string {
  return `/api/v1/channels/${encodeURIComponent(channelId)}/${tab}`;
}

export function channelDetailsPath(channelId: string): string {
  return `/api/v1/channels/${encodeURIComponent(channelId)}`;
}

/**
 * What goes on the wire for one page of a tab — 2c's "the route carries what was **asked**, the wire
 * sends what can be **honoured**" rule, applied to the sort.
 *
 * Two things a naive caller gets wrong here:
 *
 * - **The sort is dropped for `/playlists`**, because an unrecognised value there empties the list
 *   rather than being ignored ({@link channelTabSupportsSort}).
 * - **A continuation carries its own order**, exactly like a comments continuation: page 2 fetched
 *   with `sort_by=oldest` and page 2 fetched with no sort at all returned the *identical* 60 ids. So
 *   the sort is sent only on the first page, and changing it is a fresh list rather than a re-order.
 */
export function channelTabWireParams(
  tab: ChannelListTab,
  sort: ChannelVideoSort,
  continuation: string | null,
): Readonly<Record<string, string | null>> {
  if (continuation !== null && continuation !== '') return { continuation };
  if (!channelTabSupportsSort(tab) || sort === DEFAULT_CHANNEL_SORT) return {};
  return { sort_by: sort };
}

/* ------------------------------------------------------------------------------------------------
 * Paging
 * ---------------------------------------------------------------------------------------------- */

/**
 * One page of a tab. `continuation` is `null` when the response carried none, which is the **only**
 * end-of-list signal there is.
 *
 * Search pages with `page=` and runs out as a 400; a channel tab pages with an opaque token and runs
 * out by omitting it. Do not reuse either rule for the other, and in particular do not derive
 * "finished" from the page size: Khan Academy's playlists tab returned **29 rows on page 7** and then
 * eleven more full pages. Measured over 12 pages per tab — 720 videos and 359 playlists — with **zero
 * duplicate ids**, so there is no dedupe pass here either.
 */
export interface ChannelPage<T> {
  readonly items: readonly T[];
  readonly continuation: string | null;
  readonly dropped: readonly ParseError[];
}

export type ChannelVideoPage = ChannelPage<VideoSummary>;
export type ChannelPlaylistPage = ChannelPage<PlaylistSummary>;

/** The accumulated list, folded from however many pages have arrived. */
export interface ChannelListing<T> {
  readonly items: readonly T[];
  readonly pages: number;
  readonly droppedCount: number;
  /** The token for the next request, or `null` when the last page carried none. */
  readonly continuation: string | null;
}

export function foldChannelPages<T>(pages: readonly ChannelPage<T>[]): ChannelListing<T> {
  const items: T[] = [];
  let droppedCount = 0;
  for (const page of pages) {
    items.push(...page.items);
    droppedCount += page.dropped.length;
  }
  const last = pages.length === 0 ? undefined : pages[pages.length - 1];
  return {
    items,
    pages: pages.length,
    droppedCount,
    // From the *last* page only: an earlier page's token is spent, and replaying one answers
    // `500 {"error":"…Youtube API returned status code 400…"}` rather than repeating the page.
    continuation: last?.continuation ?? null,
  };
}

export function channelListingHasMore<T>(listing: ChannelListing<T>): boolean {
  return listing.continuation !== null && listing.continuation !== '';
}

/* ------------------------------------------------------------------------------------------------
 * What a row on a given tab may state
 * ---------------------------------------------------------------------------------------------- */

/**
 * The constant `lengthSeconds` every row of `/shorts` carries. **245 of 245 rows across six channels**
 * reported exactly this, including a channel with only five shorts — while the same videos fetched
 * individually are 68 s, 68 s and 72 s. So it is a placeholder rather than a measurement, and it is
 * not a zero, which means `feedRowHasDuration` cannot catch it and only knowing *which tab answered*
 * can.
 */
export const SHORTS_PLACEHOLDER_LENGTH_SEC = 60;

/**
 * What a row on this tab is allowed to state. {@link listingFacts} answers it for a feed; this adds
 * the two per-tab corrections that a payload cannot express, and both are absences the API states as
 * confident values.
 *
 * **`/shorts` carries neither a duration nor a date.** Every row reports
 * `lengthSeconds: 60` (see {@link SHORTS_PLACEHOLDER_LENGTH_SEC}) and `published: 0` — epoch zero,
 * which the instance renders as `publishedText: "56 years ago"`. Rendered faithfully that is a 1:00
 * badge and "56 years ago" on every Short, twice wrong in a confident voice. The view count *is* real
 * (241 K, 2.5 M, 685 K…) and is kept.
 *
 * The date half is already dead by the time it gets here — `asEpochSeconds` maps any `<= 0` to `null`,
 * so epoch zero never becomes a `publishedEpochSec`. It is restated anyway, because the *reason* is
 * different: that guard catches a nonsense number, this one records that the whole field is a
 * placeholder on this endpoint. If the placeholder ever changes to something that looks like a date,
 * the guard stops helping and this line still does.
 *
 * **`/streams` cannot report live state.** `liveNow` is `false` on **every row of every channel
 * measured** — including 20 Lofi Girl radios that were live at that second, and a CNN 24/7 stream —
 * so `videoBadgeOf`'s `live` arm is unreachable here. Those rows carry `lengthSeconds: 0`,
 * `viewCount: 0` and `published` = the request's own second, so the existing per-field absence rules
 * already suppress all three and the row draws no badge. That is deliberate: the trio *does* mean
 * "live right now" (5 of 5 such rows probed individually were `liveNow: true`, 0 upcoming, and the
 * trio never appeared on a row that was not live), but n = 5 on two channels is not enough to put a
 * LIVE badge on somebody else's stream. The page says it in a notice instead
 * ({@link channelTabNotes}) — §7's rule that a missing thing is stated rather than hidden.
 *
 * `scrape` is still the row's own response, so 2b's degenerate-list rule keeps working underneath:
 * a whole tab that came back with titles only is one statement, not sixty mysteriously empty rows.
 */
export function channelRowFacts(
  tab: ChannelListTab,
  video: VideoSummary,
  nowEpochSec: number,
  scrape: FeedScrape | null,
): ListingFacts {
  const facts = listingFacts(video, nowEpochSec, scrape);
  if (tab !== 'shorts') return facts;
  return { ...facts, badge: { kind: 'unknown' }, publishedEpochSec: null };
}

/**
 * A statement the tab has to make about the list it is nonetheless showing — the shape
 * `screens/feed/model.ts` established in 2b, with the two subjects that are specific to a channel.
 *
 * Each one is conditional on the measurement still holding, so it disappears the moment upstream
 * starts filling the field. That is the difference between explaining data and apologising for code.
 */
export type ChannelTabNote =
  /** `/shorts` — the duration and the date are placeholders for the whole tab. */
  | { readonly id: 'shorts-placeholders' }
  /** `/streams` — `count` rows carry nothing, which on this endpoint means they are live now. */
  | { readonly id: 'streams-live-unreported'; readonly count: number }
  /** 2b's degenerate list: no row carried a duration, a count or a date. */
  | { readonly id: 'degenerate'; readonly rows: number }
  /** R4 — records we recognised and could not use. */
  | { readonly id: 'dropped'; readonly count: number };

export function channelTabNotes(
  tab: ChannelListTab,
  videos: readonly VideoSummary[],
  nowEpochSec: number,
  scrape: FeedScrape,
  droppedCount: number,
): readonly ChannelTabNote[] {
  const notes: ChannelTabNote[] = [];

  if (tab === 'shorts' && videos.length > 0) notes.push({ id: 'shorts-placeholders' });

  if (tab === 'streams') {
    // The signature of a live row on this endpoint: no length, no views, and a date that is not a
    // date. Counted rather than tested per row, because the claim being made is about the list.
    const count = videos.filter(
      (video) =>
        video.lengthSeconds === 0 &&
        video.viewCount === 0 &&
        !feedRowHasDate(video, nowEpochSec),
    ).length;
    if (count > 0) notes.push({ id: 'streams-live-unreported', count });
  }

  if (scrape.degenerate) notes.push({ id: 'degenerate', rows: scrape.rows });
  if (droppedCount > 0) notes.push({ id: 'dropped', count: droppedCount });

  return notes;
}
